const _host = 'chappie-demo.novosense.africa';
const _company = 'wdsf';

class AuditLogger {
  constructor() {
    this.batchLogs = [];
    this.batchSize = 10;
    this.batchTimeout = 5000; // 5 seconds
    this.batchTimer = null;
  }

  // Log a user action
  async log(userId, action, details = '', targetId = null, targetType = null, competitionId = null) {
    try {
      // Get current competition from localStorage if not provided
      if (!competitionId) {
        try {
          const selectedCompetition = localStorage.getItem('selectedCompetition');
          if (selectedCompetition) {
            const competition = JSON.parse(selectedCompetition);
            competitionId = competition.id;
          }
        } catch (error) {
          console.warn('Could not retrieve competition from localStorage:', error);
        }
      }

      const logEntry = {
        userId,
        action,
        details,
        targetId,
        targetType,
        competitionId,
        timestamp: Date.now(),
        ip: await this.getClientIP(),
        userAgent: navigator.userAgent
      };

      // Add to batch
      this.batchLogs.push(logEntry);

      // Send batch if it reaches the size limit
      if (this.batchLogs.length >= this.batchSize) {
        await this.sendBatch();
      } else {
        // Set timer to send batch after timeout
        this.scheduleBatchSend();
      }
    } catch (error) {
      console.error('Audit logging error:', error);
    }
  }

  // Schedule batch send
  scheduleBatchSend() {
    if (this.batchTimer) {
      clearTimeout(this.batchTimer);
    }

    this.batchTimer = setTimeout(() => {
      this.sendBatch();
    }, this.batchTimeout);
  }

  // Send batch of logs to server
  async sendBatch() {
    if (this.batchLogs.length === 0) return;

    const logsToSend = [...this.batchLogs];
    this.batchLogs = [];

    try {
      // Try to send logs in parallel for better performance
      const promises = logsToSend.map(log => this.sendLog(log));
      await Promise.all(promises);
    } catch (error) {
      console.error('Failed to send audit logs:', error);
      // Add logs back to batch for retry
      this.batchLogs.unshift(...logsToSend);
    }
  }

  // Send individual log to server
  async sendLog(logData) {
    try {
      const response = await fetch(`https://${_host}:6003/api/${_company}/audit-logs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(logData)
      });

      if (!response.ok) {
        // If endpoint doesn't exist, don't treat it as an error
        if (response.status === 404) {
          console.log('Audit trail endpoint not available yet - audit logging will be enabled when server is restarted');
          return null;
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      // Don't throw here to avoid breaking the main application flow
      console.warn('Failed to send audit log (server may need restart):', error);
      return null;
    }
  }

  // Get client IP (best effort)
  async getClientIP() {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      return data.ip;
    } catch {
      return 'Unknown';
    }
  }

  // Flush any remaining logs before page unload
  flush() {
    if (this.batchLogs.length > 0) {
      this.sendBatch();
    }
  }

  // Convenience methods for common actions
  logLogin(userId, competitionId = null) {
    this.log(userId, 'LOGIN', 'User logged in', null, null, competitionId);
  }

  logLogout(userId, competitionId = null) {
    this.log(userId, 'LOGOUT', 'User logged out', null, null, competitionId);
  }

  logCreateCompetition(userId, competitionName, competitionId) {
    this.log(userId, 'CREATE_COMPETITION', `Created competition: ${competitionName}`, competitionId, 'competition', competitionId);
  }

  logUpdateCompetition(userId, competitionName, competitionId) {
    this.log(userId, 'UPDATE_COMPETITION', `Updated competition: ${competitionName}`, competitionId, 'competition', competitionId);
  }

  logSelectCompetition(userId, competitionName, competitionId) {
    this.log(userId, 'SELECT_COMPETITION', `Selected competition: ${competitionName}`, competitionId, 'competition', competitionId);
  }

  logCreateEvent(userId, eventName, eventId) {
    this.log(userId, 'CREATE_EVENT', `Created event: ${eventName}`, eventId, 'event');
  }

  logUpdateEvent(userId, eventName, eventId) {
    this.log(userId, 'UPDATE_EVENT', `Updated event: ${eventName}`, eventId, 'event');
  }

  logDeleteEvent(userId, eventName, eventId) {
    this.log(userId, 'DELETE_EVENT', `Deleted event: ${eventName}`, eventId, 'event');
  }

  logCreateDancer(userId, dancerName, dancerId) {
    this.log(userId, 'CREATE_DANCER', `Created dancer: ${dancerName}`, dancerId, 'dancer');
  }

  logUpdateDancer(userId, dancerName, dancerId) {
    this.log(userId, 'UPDATE_DANCER', `Updated dancer: ${dancerName}`, dancerId, 'dancer');
  }

  logDeleteDancer(userId, dancerName, dancerId) {
    this.log(userId, 'DELETE_DANCER', `Deleted dancer: ${dancerName}`, dancerId, 'dancer');
  }

  logAddDancerToEvent(userId, dancerName, eventId, eventName) {
    this.log(userId, 'ADD_DANCER_TO_EVENT', `Added ${dancerName} to ${eventName}`, eventId, 'event');
  }

  logRemoveDancerFromEvent(userId, dancerName, eventId, eventName) {
    this.log(userId, 'REMOVE_DANCER_FROM_EVENT', `Removed ${dancerName} from ${eventName}`, eventId, 'event');
  }

  logUploadImage(userId, imageType, imageId) {
    this.log(userId, 'UPLOAD_IMAGE', `Uploaded ${imageType} image`, imageId, 'image');
  }

  logDeleteImage(userId, imageType, imageId) {
    this.log(userId, 'DELETE_IMAGE', `Deleted ${imageType} image`, imageId, 'image');
  }

  // Additional methods for comprehensive CRUD coverage
  logStartCompetition(userId, competitionId) {
    this.log(userId, 'START_COMPETITION', 'Started competition', competitionId, 'competition', competitionId);
  }

  logStopCompetition(userId, competitionId) {
    this.log(userId, 'STOP_COMPETITION', 'Stopped competition', competitionId, 'competition', competitionId);
  }

  logHeatGeneration(userId, eventId, eventName, heatCount) {
    this.log(userId, 'GENERATE_HEATS', `Generated ${heatCount} heats for ${eventName}`, eventId, 'event');
  }

  logUpdateDisplay(userId, eventId, eventName, displayType) {
    this.log(userId, 'UPDATE_DISPLAY', `Updated ${displayType} display for ${eventName}`, eventId, 'event');
  }

  logViewEvent(userId, eventId, eventName) {
    this.log(userId, 'VIEW_EVENT', `Viewed event details for ${eventName}`, eventId, 'event');
  }

  logExportData(userId, dataType, recordCount) {
    this.log(userId, 'EXPORT_DATA', `Exported ${recordCount} ${dataType} records`, null, 'data_export');
  }
}

// Create a singleton instance
const auditLogger = new AuditLogger();

// Auto-flush logs before page unload
window.addEventListener('beforeunload', () => {
  auditLogger.flush();
});

// Auto-flush logs when page becomes hidden (mobile app switching)
document.addEventListener('visibilitychange', () => {
  if (document.hidden) {
    auditLogger.flush();
  }
});

export default auditLogger;